// Motor de precios y promociones — gemelo en TypeScript del de C#.
//
// El mismo cálculo vive en dos lenguajes a propósito y no por descuido: en la
// caja y en la nube corre en C#, y acá corre en el navegador para poder mostrar
// el total mientras el cajero arma el ticket, sin ir y volver al servidor por
// cada línea.
//
// Las dos implementaciones tienen que dar EXACTAMENTE lo mismo hasta el último
// centavo. Si divergen, la pantalla muestra un precio y la caja cobra otro, y
// eso se descubre con un cliente adelante. Lo que lo evita es
// contratos/precios-golden.json: el mismo corpus lo corren xUnit y Vitest, así
// que tocar una implementación y no la otra rompe un test.
//
// De ahí las reglas de este archivo, que son las mismas que las del de C#:
//
//   1. Aritmética ENTERA. Centavos y milésimas, nunca decimales. JavaScript no
//      tiene decimal, así que la única forma de coincidir con C# es que los dos
//      hagan enteros.
//   2. Cero dependencias. Ni fetch, ni Date.now(), ni configuración. El momento
//      entra por parámetro.
//
// IMPORTANTE al portar un cambio: si se toca una función acá, hay que tocar la
// misma en Backend/src/Fruver.Application/Precios/MotorPrecios.cs, y agregar el
// caso al corpus.

export type TipoPromocion =
  | 'NxM'
  | 'CantidadPorPrecio'
  | 'PorcentajeDescuento'
  | 'ImporteDescuento'
  | 'SegundaUnidadDescuento'

export type ModoPromocion = 'Bloques' | 'Acumulativo'
export type AlcancePromocion = 'Producto' | 'Categoria' | 'Lista'

export interface Promocion {
  id: number
  version: number
  nombre: string

  tipo: TipoPromocion
  modo?: ModoPromocion
  alcance: AlcancePromocion

  productoId?: number | null
  categoriaId?: number | null
  productoIds?: number[]

  /** N del «NxM» y del «cantidad por precio», en milésimas. */
  cantidadRequeridaMilesimas?: number
  /** M del «NxM», en milésimas. */
  cantidadPagaMilesimas?: number
  /** Precio del grupo completo, en centavos. */
  precioGrupoCentavos?: number
  /** Porcentaje en centésimas de punto: 2000 son 20,00%. */
  porcentajeBps?: number
  /** Importe fijo a descontar, en centavos. */
  importeCentavos?: number

  /** ISO corto (2026-08-31). */
  desdeFecha?: string | null
  hastaFecha?: string | null
  /** Números de día como los de Date.getDay(): 0 domingo, 3 miércoles. */
  diasSemana?: number[]
  /** Minutos desde medianoche. */
  desdeMinuto?: number | null
  hastaMinuto?: number | null

  prioridad: number
  combinable: boolean
  activa: boolean
}

export interface LineaAEvaluar {
  orden: number
  productoId: number
  categoriaId: number
  cantidadMilesimas: number
  precioUnitarioCentavos: number
  decimalesCantidad: number
  brutoCentavos: number
}

export interface LineaEvaluada {
  orden: number
  brutoCentavos: number
  descuentoCentavos: number
  totalCentavos: number
  promocionId: number | null
  promocionVersion: number | null
  promocionNombre: string | null
  detalle: string | null
}

export interface ResultadoPrecios {
  lineas: LineaEvaluada[]
  brutoCentavos: number
  descuentoCentavos: number
  totalCentavos: number
}

// ── Motor ───────────────────────────────────────────────────────────────────

export function evaluarPrecios(
  lineas: LineaAEvaluar[],
  promociones: Promocion[],
  momentoLocal: Date,
): ResultadoPrecios {
  // El orden de las líneas se fija UNA vez y todo lo de abajo lo respeta: el
  // prorrateo de un descuento de grupo reparte el resto de la división entera
  // recorriendo las líneas en este orden, así que si el orden no fuera
  // explícito el mismo ticket podría dar centavos distintos que en C#.
  const orden = [...lineas].sort((a, b) => a.orden - b.orden)

  const evaluadas: LineaEvaluada[] = orden.map((l) => ({
    orden: l.orden,
    brutoCentavos: l.brutoCentavos,
    descuentoCentavos: 0,
    totalCentavos: l.brutoCentavos,
    promocionId: null,
    promocionVersion: null,
    promocionNombre: null,
    detalle: null,
  }))

  // Una línea que recibió una promoción NO combinable queda cerrada: no la
  // alcanza ninguna promoción posterior.
  const cerradas = orden.map(() => false)

  // Orden por prioridad y, a igual prioridad, por id. El desempate por id no
  // es cosmético: sin él el resultado dependería del orden en que llegó el
  // arreglo, y la misma venta daría distinto en dos pantallas.
  const vigentes = promociones
    .filter((p) => estaVigente(p, momentoLocal))
    .sort((a, b) => a.prioridad - b.prioridad || a.id - b.id)

  // Se recorre por PROMOCIÓN y no por línea, igual que en C#, y esa es la
  // diferencia que hace que las promos por cantidad funcionen en una
  // verdulería: «3 kg de papa a $2.500» con 3,4 kg en dos bolsas entra como
  // dos líneas de 1,700 kg, y evaluando línea por línea ninguna llegaba a los
  // 3 kg. El cartel decía una cosa y la caja cobraba otra.
  //
  // Las líneas siguen separadas en pantalla y en el ticket: se agrupa la
  // EVALUACIÓN, no la presentación.
  for (const promo of vigentes) {
    const alcanzadas: number[] = []

    for (let i = 0; i < orden.length; i++) {
      if (cerradas[i]) continue
      const l = orden[i]!
      if (alcanza(promo, l.productoId, l.categoriaId)) alcanzadas.push(i)
    }

    if (alcanzadas.length === 0) continue

    if (promo.tipo === 'PorcentajeDescuento') {
      // Por línea, porque un porcentaje es lineal: agrupar daría el mismo
      // número salvo por el redondeo al centavo.
      for (const i of alcanzadas) {
        aplicar(evaluadas, cerradas, promo, [i], porcentaje(promo, evaluadas[i]!.totalCentavos))
      }
    } else if (promo.tipo === 'ImporteDescuento') {
      // UNA vez por ticket, prorrateada. «$500 de descuento» es un importe, no
      // un importe por renglón: devolverlo por línea descontaba $2.000 en una
      // categoría con cuatro productos en el ticket.
      aplicar(evaluadas, cerradas, promo, alcanzadas, {
        centavos: promo.importeCentavos ?? 0,
        detalle: promo.nombre,
      })
    } else {
      // Las de cantidad se evalúan por PRODUCTO: es la cantidad de papa lo que
      // la promo mira, no la cantidad de esta bolsa.
      const porProducto = new Map<number, number[]>()

      for (const i of alcanzadas) {
        const id = orden[i]!.productoId
        const grupo = porProducto.get(id)

        if (grupo) grupo.push(i)
        else porProducto.set(id, [i])
      }

      for (const indices of porProducto.values()) {
        aplicar(evaluadas, cerradas, promo, indices, porCantidad(promo, consolidar(orden, indices)))
      }
    }
  }

  return {
    lineas: evaluadas,
    brutoCentavos: suma(evaluadas.map((l) => l.brutoCentavos)),
    descuentoCentavos: suma(evaluadas.map((l) => l.descuentoCentavos)),
    totalCentavos: suma(evaluadas.map((l) => l.totalCentavos)),
  }
}

/**
 * Las líneas de un mismo producto, como si fueran una sola.
 *
 * El precio unitario y los decimales salen de la PRIMERA línea del grupo: son
 * el mismo producto en el mismo ticket, así que comparten el precio de lista,
 * y tomar el de la primera deja el resultado determinístico si algún día no lo
 * comparten.
 */
function consolidar(orden: LineaAEvaluar[], indices: number[]): LineaAEvaluar {
  const primera = orden[indices[0]!]!

  return {
    ...primera,
    cantidadMilesimas: suma(indices.map((i) => orden[i]!.cantidadMilesimas)),
    brutoCentavos: suma(indices.map((i) => orden[i]!.brutoCentavos)),
  }
}

/**
 * Aplica un descuento a un grupo de líneas, prorrateándolo.
 *
 * La suma de los descuentos por línea tiene que dar EXACTAMENTE el descuento
 * del grupo: si no, el total del ticket no cierra contra sus renglones.
 */
function aplicar(
  evaluadas: LineaEvaluada[],
  cerradas: boolean[],
  promo: Promocion,
  indices: number[],
  aplicacion: { centavos: number; detalle?: string | null },
) {
  if (aplicacion.centavos <= 0) return

  // El tope de cada línea es lo que le queda: el descuento no puede dejarla en
  // negativo, y una línea negativa contamina el total y todos los reportes.
  const topes = indices.map((i) => evaluadas[i]!.totalCentavos)
  const partes = repartir(aplicacion.centavos, topes)

  for (let k = 0; k < indices.length; k++) {
    const parte = partes[k]!
    if (parte <= 0) continue

    const linea = evaluadas[indices[k]!]!

    linea.descuentoCentavos += parte
    linea.totalCentavos -= parte

    // Se registra la PRIMERA que aplicó: es la de mayor prioridad, y es la que
    // el cliente vio en el cartel. Queda en TODAS las líneas que recibieron
    // parte del descuento: si no, el renglón de la segunda bolsa no tendría
    // con qué explicar su precio.
    if (linea.promocionId === null) {
      linea.promocionId = promo.id
      linea.promocionVersion = promo.version
      linea.promocionNombre = promo.nombre
    }

    linea.detalle = linea.detalle
      ? `${linea.detalle} + ${aplicacion.detalle}`
      : (aplicacion.detalle ?? null)

    // Si no es combinable, esa línea no recibe nada más.
    if (!promo.combinable) cerradas[indices[k]!] = true
  }
}

/**
 * Reparte un descuento entre varias líneas, sin pasarse de ninguna.
 *
 * Proporcional a lo que le queda a cada línea, y el resto de la división
 * entera centavo por centavo recorriendo las líneas EN ORDEN. Gemelo exacto de
 * `Repartir` en MotorPrecios.cs: el corpus golden compara los dos.
 */
export function repartir(total: number, topes: number[]): number[] {
  const partes = topes.map(() => 0)

  const capacidad = suma(topes)
  if (capacidad <= 0 || total <= 0) return partes

  const aRepartir = Math.min(total, capacidad)
  let asignado = 0

  for (let i = 0; i < topes.length; i++) {
    // Piso y no redondeo, para no pasarse: lo que sobra se reparte abajo.
    partes[i] = Math.floor((aRepartir * topes[i]!) / capacidad)
    asignado += partes[i]!
  }

  let resto = aRepartir - asignado

  for (let i = 0; resto > 0 && i < topes.length; i++) {
    const lugar = topes[i]! - partes[i]!
    if (lugar <= 0) continue

    const suma = Math.min(lugar, resto)
    partes[i] = partes[i]! + suma
    resto -= suma
  }

  return partes
}

// ── Vigencia y alcance ──────────────────────────────────────────────────────

export function alcanza(p: Promocion, productoId: number, categoriaId: number): boolean {
  if (p.alcance === 'Producto') return p.productoId === productoId
  if (p.alcance === 'Categoria') return p.categoriaId === categoriaId
  if (p.alcance === 'Lista') return (p.productoIds ?? []).includes(productoId)
  return false
}

export function estaVigente(p: Promocion, momentoLocal: Date): boolean {
  if (!p.activa) return false

  // La fecha se compara como texto ISO corto para no depender de la zona
  // horaria del navegador: `new Date('2026-08-31')` se interpreta como UTC y
  // en Argentina eso es el 30 a las 21:00, así que una promo que vence el 31
  // dejaría de aplicar un día antes.
  const fecha = fechaISO(momentoLocal)

  if (p.desdeFecha && fecha < p.desdeFecha) return false
  if (p.hastaFecha && fecha > p.hastaFecha) return false

  const dias = p.diasSemana ?? []
  if (dias.length > 0 && !dias.includes(momentoLocal.getDay())) return false

  const minuto = momentoLocal.getHours() * 60 + momentoLocal.getMinutes()

  // La franja puede cruzar la medianoche (de 22:00 a 02:00): hay verdulerías
  // que abren de noche, y una franja invertida sin manejar no aplicaría nunca.
  if (p.desdeMinuto != null && p.hastaMinuto != null) {
    return p.desdeMinuto <= p.hastaMinuto
      ? minuto >= p.desdeMinuto && minuto <= p.hastaMinuto
      : minuto >= p.desdeMinuto || minuto <= p.hastaMinuto
  }

  if (p.desdeMinuto != null && minuto < p.desdeMinuto) return false
  if (p.hastaMinuto != null && minuto > p.hastaMinuto) return false

  return true
}

// ── Cálculo por tipo ────────────────────────────────────────────────────────

interface Aplicacion {
  centavos: number
  detalle?: string
}

// Las promociones que miran la CANTIDAD. Entra el grupo consolidado del
// producto, no una línea suelta (ver evaluarPrecios).
function porCantidad(p: Promocion, grupo: LineaAEvaluar): Aplicacion {
  switch (p.tipo) {
    case 'NxM':
      return nxm(p, grupo)
    case 'CantidadPorPrecio':
      return cantidadPorPrecio(p, grupo)
    case 'SegundaUnidadDescuento':
      return segundaUnidad(p, grupo)
    default:
      return { centavos: 0 }
  }
}

function porcentaje(p: Promocion, baseCentavos: number): Aplicacion {
  const bps = p.porcentajeBps ?? 0
  if (bps <= 0) return { centavos: 0 }

  return {
    centavos: redondear(baseCentavos * bps, 10000),
    detalle: `${p.nombre} (${formatearPorcentaje(bps)}%)`,
  }
}

function nxm(p: Promocion, linea: LineaAEvaluar): Aplicacion {
  const pide = p.cantidadRequeridaMilesimas ?? 0
  const paga = p.cantidadPagaMilesimas ?? 0

  if (pide <= 0 || paga <= 0 || paga >= pide) return { centavos: 0 }

  const grupos = Math.floor(linea.cantidadMilesimas / pide)
  if (grupos === 0) return { centavos: 0 }

  let gratis = grupos * (pide - paga)

  if (p.modo === 'Acumulativo') {
    const efectivo = Math.floor((linea.cantidadMilesimas * paga) / pide)
    gratis = linea.cantidadMilesimas - efectivo
  }

  return {
    centavos: redondear(gratis * linea.precioUnitarioCentavos, 1000),
    detalle:
      p.modo === 'Acumulativo'
        ? p.nombre
        : `${p.nombre} (${grupos} grupo${grupos === 1 ? '' : 's'})`,
  }
}

function cantidadPorPrecio(p: Promocion, linea: LineaAEvaluar): Aplicacion {
  const pide = p.cantidadRequeridaMilesimas ?? 0
  const precioGrupo = p.precioGrupoCentavos ?? 0

  if (pide <= 0 || precioGrupo <= 0) return { centavos: 0 }

  const grupos = Math.floor(linea.cantidadMilesimas / pide)
  if (grupos === 0) return { centavos: 0 }

  if (p.modo === 'Acumulativo') {
    // El cartel dice «llevando 3 kg o más, a $833 el kilo»: toda la cantidad
    // va al precio unitario de la promo, incluido el sobrante.
    const conPromo = redondear(linea.cantidadMilesimas * precioGrupo, pide)
    return { centavos: Math.max(0, linea.brutoCentavos - conPromo), detalle: p.nombre }
  }

  // Bloques: el cartel dice «3 kg por $2.500». Promete un precio por LLEVAR
  // TRES, no un precio por kilo, así que el sobrante va a precio de lista.
  const enGrupos = grupos * pide
  const sobrante = linea.cantidadMilesimas - enGrupos

  const conBloques =
    grupos * precioGrupo + redondear(sobrante * linea.precioUnitarioCentavos, 1000)

  return {
    centavos: Math.max(0, linea.brutoCentavos - conBloques),
    detalle: `${p.nombre} (${grupos}×)`,
  }
}

function segundaUnidad(p: Promocion, linea: LineaAEvaluar): Aplicacion {
  const bps = p.porcentajeBps ?? 0
  if (bps <= 0) return { centavos: 0 }

  // El grupo por defecto es de 2: la «segunda unidad». Si se configura otro,
  // vale el configurado («la tercera al 70%»).
  const grupo = (p.cantidadRequeridaMilesimas ?? 0) > 0 ? p.cantidadRequeridaMilesimas! : 2000

  const grupos = Math.floor(linea.cantidadMilesimas / grupo)
  if (grupos === 0) return { centavos: 0 }

  const conDescuento = grupos * unidadMinima(linea.decimalesCantidad)
  const base = redondear(conDescuento * linea.precioUnitarioCentavos, 1000)

  return { centavos: redondear(base * bps, 10000), detalle: `${p.nombre} (${grupos}×)` }
}

// ── Aritmética ──────────────────────────────────────────────────────────────

/**
 * División entera con redondeo al más cercano y, en el empate, para arriba.
 *
 * Es la misma implementación que en C#, a propósito y línea por línea: no se
 * usa Math.round porque en JavaScript Math.round(-0.5) da -0 y en C#
 * MidpointRounding.AwayFromZero da -1, y esa diferencia haría divergir las dos
 * implementaciones justo en los importes que caen en el medio.
 */
export function redondear(numerador: number, denominador: number): number {
  if (denominador === 0) return 0

  const negativo = numerador < 0 !== denominador < 0

  const n = Math.abs(numerador)
  const d = Math.abs(denominador)

  const resultado = Math.floor((n + Math.floor(d / 2)) / d)

  return negativo ? -resultado : resultado
}

/** Unidad mínima de cantidad, en milésimas, según los decimales de la unidad. */
export function unidadMinima(decimales: number): number {
  const d = Math.min(Math.max(decimales, 0), 3)

  if (d === 0) return 1000
  if (d === 1) return 100
  if (d === 2) return 10
  return 1
}

function suma(valores: number[]): number {
  return valores.reduce((a, b) => a + b, 0)
}

function fechaISO(d: Date): string {
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const dia = String(d.getDate()).padStart(2, '0')

  return `${d.getFullYear()}-${mes}-${dia}`
}

function formatearPorcentaje(bps: number): string {
  // Sin decimales cuando es redondo (20%), con hasta dos cuando no (12,5%).
  const valor = bps / 100

  return Number.isInteger(valor)
    ? String(valor)
    : valor.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',')
}
